import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { ArrowLeft, Briefcase, Plus, MoreHorizontal, Edit, Trash2, Check, ChevronsUpDown, Eye, Upload, X, File, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ExpedienteDigital = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [fichasCatastrales, setFichasCatastrales] = useState([]);
  const [expedienteFilters, setExpedienteFilters] = useState({ rif: "", nombre: "", codigo_catastral: "" });
  const [codigoCatastralPopoverOpen, setCodigoCatastralPopoverOpen] = useState(false);
  const [expedienteCodCatBusqueda, setExpedienteCodCatBusqueda] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/catastro/expediente-digital`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  const fetchFichasCatastrales = async () => {
    try {
      const response = await axios.get(`${API}/api/catastro/ficha-catastral`, { headers });
      setFichasCatastrales(response.data || []);
    } catch (err) {
      console.log("Error al cargar fichas catastrales:", err);
    }
  };

  useEffect(() => { fetchData(); fetchFichasCatastrales(); }, []);

  const filteredData = data.filter(item => {
    const ef = expedienteFilters;
    if (ef.rif && !(item.rif || "").toLowerCase().includes(ef.rif.toLowerCase())) return false;
    if (ef.nombre && !(item.nombre_razon_social || "").toLowerCase().includes(ef.nombre.toLowerCase())) return false;
    if (ef.codigo_catastral && !(item.codigo_catastral || "").toLowerCase().includes(ef.codigo_catastral.toLowerCase())) return false;
    return true;
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/catastro/expediente-digital`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/catastro/expediente-digital/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/expediente-digital/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  const handlePdfUpload = (field, nameField) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') { toast.error("Solo se permiten archivos PDF"); return; }
      if (file.size > 10 * 1024 * 1024) { toast.error("El archivo no debe superar 10MB"); return; }
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.result, [nameField]: file.name }));
        toast.success(`Archivo "${file.name}" cargado exitosamente`);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePdf = (field, nameField) => () => {
    setFormData(prev => ({ ...prev, [field]: null, [nameField]: null }));
    toast.info("Archivo eliminado");
  };

  const handleViewPdf = (base64Data, fileName) => () => {
    if (base64Data) {
      const w = window.open();
      w.document.write(`<html><head><title>${fileName || 'Documento PDF'}</title></head><body style="margin:0;padding:0;"><embed src="${base64Data}" type="application/pdf" width="100%" height="100%" /></body></html>`);
    }
  };

  const handleCodigoCatastralChange = async (codigoCatastral) => {
    setFormData(prev => ({...prev, codigo_catastral: codigoCatastral}));
    if (codigoCatastral) {
      const fichaEncontrada = fichasCatastrales.find(f => f.codigo_catastral === codigoCatastral);
      if (fichaEncontrada) {
        setFormData(prev => ({ ...prev, codigo_catastral: codigoCatastral, rif: fichaEncontrada.rif || "", nombre_razon_social: fichaEncontrada.nombre_razon_social || "" }));
        toast.success("Datos del contribuyente cargados automáticamente");
      }
    } else {
      setFormData(prev => ({...prev, rif: "", nombre_razon_social: ""}));
    }
  };

  const pdfDocuments = [
    { field: "copia_cedula_identidad", nameField: "copia_cedula_nombre", label: "Copia Cédula de Identidad" },
    { field: "copia_rif", nameField: "copia_rif_nombre", label: "Copia RIF" },
    { field: "documento_registro", nameField: "documento_registro_nombre", label: "Documento de Registro" },
    { field: "plano", nameField: "plano_nombre", label: "Plano" },
    { field: "levantamiento_topografico", nameField: "levantamiento_topografico_nombre", label: "Levantamiento Topográfico" },
  ];

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center"><Briefcase className="w-6 h-6 text-teal-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Expediente Digital</h1>
          <p className="text-slate-500">Gestión de expediente digital</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredData.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{data.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          {Object.values(expedienteFilters).some(v => v !== "") && (
            <Button variant="ghost" size="sm" onClick={() => setExpedienteFilters({ rif: "", nombre: "", codigo_catastral: "" })} className="text-xs text-slate-500 hover:text-slate-700" data-testid="btn-limpiar-filtros-expediente">Limpiar filtros</Button>
          )}
        </div>
        <Button onClick={() => { setEditingItem(null); setFormData({}); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo"><Plus className="w-4 h-4 mr-2" />Nuevo Registro</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">RIF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nombre / Razón Social</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Código Catastral</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Cédula</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">RIF</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Registro</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Plano</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Topográfico</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
                <tr className="border-b bg-white">
                  <th className="px-2 py-2"><Input placeholder="Filtrar..." value={expedienteFilters.rif} onChange={(e) => setExpedienteFilters({...expedienteFilters, rif: e.target.value})} className="h-8 text-xs" data-testid="filter-expediente-rif" /></th>
                  <th className="px-2 py-2"><Input placeholder="Filtrar..." value={expedienteFilters.nombre} onChange={(e) => setExpedienteFilters({...expedienteFilters, nombre: e.target.value})} className="h-8 text-xs" data-testid="filter-expediente-nombre" /></th>
                  <th className="px-2 py-2"><Input placeholder="Filtrar..." value={expedienteFilters.codigo_catastral} onChange={(e) => setExpedienteFilters({...expedienteFilters, codigo_catastral: e.target.value})} className="h-8 text-xs" data-testid="filter-expediente-codigo" /></th>
                  <th className="px-2 py-2"></th><th className="px-2 py-2"></th><th className="px-2 py-2"></th><th className="px-2 py-2"></th><th className="px-2 py-2"></th><th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.rif}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.nombre_razon_social}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.codigo_catastral || "-"}</td>
                      {["copia_cedula_identidad", "copia_rif", "documento_registro", "plano", "levantamiento_topografico"].map(field => (
                        <td key={field} className="px-4 py-3 text-center">
                          {item[field] ? (
                            <Badge className="bg-green-100 text-green-800 cursor-pointer" onClick={() => handleViewPdf(item[field], item[`${field}_nombre`] || field)()}>
                              <Eye className="w-3 h-3 mr-1" /> Ver
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-500">Sin archivo</Badge>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingItem(item); setFormData(item); setDialogOpen(true); }}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? "Editar Expediente Digital" : "Nuevo Registro - Expediente Digital"}</DialogTitle></DialogHeader>
          <div className="py-4 space-y-6">
            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Briefcase className="w-4 h-4" />Datos del Contribuyente</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Código Catastral <span className="text-red-500">*</span></Label>
                  <Popover open={codigoCatastralPopoverOpen} onOpenChange={setCodigoCatastralPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" aria-expanded={codigoCatastralPopoverOpen} className="w-full justify-between font-normal" data-testid="form-codigo-catastral-expediente">
                        {formData.codigo_catastral || "Buscar código catastral..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Buscar por código o nombre..." value={expedienteCodCatBusqueda} onValueChange={setExpedienteCodCatBusqueda} />
                        <CommandList>
                          <CommandEmpty>No se encontraron resultados</CommandEmpty>
                          <CommandGroup>
                            {fichasCatastrales.filter(f => { const b = expedienteCodCatBusqueda.toLowerCase(); return f.codigo_catastral?.toLowerCase().includes(b) || f.nombre_razon_social?.toLowerCase().includes(b) || f.rif?.toLowerCase().includes(b); }).map(f => (
                              <CommandItem key={f.id} value={f.codigo_catastral} onSelect={() => { handleCodigoCatastralChange(f.codigo_catastral); setCodigoCatastralPopoverOpen(false); setExpedienteCodCatBusqueda(""); }}>
                                <Check className={`mr-2 h-4 w-4 ${formData.codigo_catastral === f.codigo_catastral ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col"><span className="font-medium">{f.codigo_catastral}</span><span className="text-xs text-slate-500">{f.nombre_razon_social} - {f.rif}</span></div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2"><Label>RIF</Label><Input value={formData.rif || ""} readOnly className="bg-slate-100" placeholder="Se autocompleta al seleccionar código" data-testid="form-rif-expediente" /></div>
                <div className="space-y-2"><Label>Nombre/Razón Social</Label><Input value={formData.nombre_razon_social || ""} readOnly className="bg-slate-100" placeholder="Se autocompleta al seleccionar código" data-testid="form-nombre-expediente" /></div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <h3 className="font-semibold text-blue-700 flex items-center gap-2"><FileText className="w-4 h-4" />Documentos del Expediente (PDF)</h3>
              <div className="grid grid-cols-1 gap-4">
                {pdfDocuments.map(doc => (
                  <div key={doc.field} className="bg-white p-4 rounded-lg border border-blue-200">
                    <Label className="font-medium">{doc.label}</Label>
                    <div className="flex items-center gap-3 mt-2">
                      {formData[doc.field] ? (
                        <div className="flex items-center gap-2 flex-1">
                          <File className="w-5 h-5 text-red-500" />
                          <span className="text-sm text-slate-600 truncate flex-1">{formData[doc.nameField] || "documento.pdf"}</span>
                          <Button size="sm" variant="outline" onClick={handleViewPdf(formData[doc.field], formData[doc.nameField])}><Eye className="w-4 h-4 mr-1" /> Ver</Button>
                          <Button size="sm" variant="outline" className="text-red-500" onClick={handleRemovePdf(doc.field, doc.nameField)}><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer bg-blue-100 hover:bg-blue-200 px-4 py-2 rounded-lg transition-colors">
                          <Upload className="w-4 h-4 text-blue-600" /><span className="text-sm text-blue-700">Seleccionar PDF</span>
                          <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload(doc.field, doc.nameField)} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea value={formData.observaciones || ""} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} placeholder="Observaciones adicionales..." rows={3} data-testid="form-observaciones-expediente" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpedienteDigital;
